import React, { useState } from 'react';
import { Select, Space, Statistic, Table, Tabs, Tag, Typography } from 'antd';
import { useListingCompanies } from '../../hooks/useCompanies';
import { useAssignmentPerformance } from '../../hooks/useStudioOps';

function openLabel(minutes) {
    if (minutes == null) return '';
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

function currentTask(row) {
    if (!row.current) return '—';
    const name = row.current.taskName || '—';
    const open = openLabel(row.current.openMinutes);
    return open ? `${name} · ${open}` : name;
}

const STORAGE_KEY = 'assignmentPerformance.companyCodes';

function loadCompanyCodes() {
    try {
        const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(value) ? value.map(String).filter(Boolean) : [];
    } catch (error) {
        return [];
    }
}

function hourLabel(iso) {
    return new Date(iso).toLocaleTimeString('en-GB', {
        timeZone: 'Asia/Kolkata',
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h12',
    });
}

const CLIENT_COLORS = ['#2e90fa', '#12b76a', '#f79009', '#f04438', '#7a5af8', '#6172f3', '#15b79e', '#eaaa08', '#ef6820', '#667085'];

function queueClients(slots) {
    const totals = new Map();
    for (const slot of slots || []) {
        for (const [code, count] of Object.entries(slot.counts || {})) {
            totals.set(code, (totals.get(code) || 0) + count);
        }
    }
    return [...totals.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([code]) => code);
}

function Last24Hours({ slots }) {
    const [hover, setHover] = useState(null);
    const data = slots || [];
    const clients = queueClients(data);
    if (!data.length) return null;
    const max = Math.max(1, ...data.map(slot => slot.total || 0));
    const width = 760;
    const height = 200;
    const pad = { left: 28, right: 8, top: 18, bottom: 28 };
    const innerWidth = width - pad.left - pad.right;
    const innerHeight = height - pad.top - pad.bottom;
    const slotWidth = innerWidth / data.length;
    const barWidth = Math.max(4, slotWidth * 0.7);
    const hovered = hover != null ? data[hover] : null;

    return (
        <div>
            <Typography.Text strong>Last 24 hours</Typography.Text>
            <div>
                <Typography.Text type="secondary">
                    SKUs waiting for DD at each hour (IST). A SKU approved later still counts at the hours it was waiting. The number above a bar is all clients combined.
                </Typography.Text>
            </div>
            <Space size={12} wrap style={{ margin: '8px 0' }}>
                {clients.map((code, index) => (
                    <span key={code} style={{ fontSize: 12 }}>
                        <span
                            style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                marginRight: 4,
                                background: CLIENT_COLORS[index % CLIENT_COLORS.length],
                            }}
                        />
                        {code}
                    </span>
                ))}
            </Space>
            <div style={{ position: 'relative', overflowX: 'auto' }} onMouseLeave={() => setHover(null)}>
                <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="DD queue over the last 24 hours">
                    {data.map((slot, index) => {
                        const x = pad.left + slotWidth * index + (slotWidth - barWidth) / 2;
                        let y = pad.top + innerHeight;
                        const showLabel = index % 3 === 0 || index === data.length - 1;
                        return (
                            <g key={slot.start} onMouseEnter={() => setHover(index)}>
                                <rect x={pad.left + slotWidth * index} y={pad.top} width={slotWidth} height={innerHeight} fill="transparent" />
                                {clients.map((code, colorIndex) => {
                                    const value = slot.counts?.[code] || 0;
                                    const barHeight = (value / max) * innerHeight;
                                    y -= barHeight;
                                    if (!barHeight) return null;
                                    return (
                                        <rect
                                            key={code}
                                            x={x}
                                            y={y}
                                            width={barWidth}
                                            height={barHeight}
                                            fill={CLIENT_COLORS[colorIndex % CLIENT_COLORS.length]}
                                        />
                                    );
                                })}
                                <text
                                    x={pad.left + slotWidth * index + slotWidth / 2}
                                    y={Math.max(10, y - 2)}
                                    textAnchor="middle"
                                    fontSize="9"
                                    fill="#344054"
                                >
                                    {slot.total || 0}
                                </text>
                                {showLabel ? (
                                    <text
                                        x={pad.left + slotWidth * index + slotWidth / 2}
                                        y={height - 8}
                                        textAnchor="middle"
                                        fontSize="10"
                                        fill="#667085"
                                    >
                                        {hourLabel(slot.start)}
                                    </text>
                                ) : null}
                            </g>
                        );
                    })}
                </svg>
                {hovered ? (
                    <div
                        style={{
                            position: 'absolute',
                            left: 8,
                            top: 8,
                            background: '#fff',
                            border: '1px solid #e4e7ec',
                            borderRadius: 4,
                            padding: '6px 8px',
                            fontSize: 12,
                        }}
                    >
                        <div>{hourLabel(hovered.start)} IST</div>
                        <div>Total {hovered.total || 0}</div>
                        {clients.filter(code => hovered.counts?.[code]).map(code => (
                            <div key={code}>
                                {code}: {hovered.counts[code]}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

const ACTIVITY_COLORS = {
    MODEL_ASSIGNED: '#2e90fa',
    ANGLE_PRESET_ASSIGNED: '#12b76a',
    MARKED_AS_SUPPORT: '#f79009',
    REMOVED_FROM_SUPPORT: '#98a2b3',
};

const ACTIVITY_LABELS = {
    MODEL_ASSIGNED: 'Model',
    ANGLE_PRESET_ASSIGNED: 'Angle preset',
    MARKED_AS_SUPPORT: 'Marked support',
    REMOVED_FROM_SUPPORT: 'Unmarked support',
};

function activityLabel(type) {
    return ACTIVITY_LABELS[type] || type;
}

function stackTotal(counts) {
    return Object.values(counts || {}).reduce((sum, count) => sum + count, 0);
}

function presentEventTypes(slots) {
    const present = new Set();
    for (const slot of slots) {
        for (const [type, count] of Object.entries(slot.counts || {})) {
            if (count) present.add(type);
        }
    }
    const known = Object.keys(ACTIVITY_COLORS).filter(type => present.has(type));
    const extra = [...present].filter(type => !ACTIVITY_COLORS[type]);
    return [...known, ...extra];
}

function ActivityChart({ histogram }) {
    const [hover, setHover] = useState(null);
    const slots = histogram || [];
    if (!slots.length) return null;
    const types = presentEventTypes(slots);
    const max = Math.max(1, ...slots.map(slot => stackTotal(slot.counts)));
    const width = Math.max(640, slots.length * 18);
    const height = 168;
    const pad = { left: 28, right: 8, top: 8, bottom: 28 };
    const innerWidth = width - pad.left - pad.right;
    const innerHeight = height - pad.top - pad.bottom;
    const slotWidth = innerWidth / slots.length;
    const barWidth = Math.max(3, slotWidth * 0.72);
    const labelEvery = slots.length > 16 ? 4 : 2;
    const hovered = hover != null ? slots[hover] : null;

    return (
        <div>
            <Space size={12} wrap style={{ marginBottom: 8 }}>
                {types.map(type => (
                    <span key={type} style={{ fontSize: 12 }}>
                        <span
                            style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                marginRight: 4,
                                background: ACTIVITY_COLORS[type] || '#98a2b3',
                            }}
                        />
                        {activityLabel(type)}
                    </span>
                ))}
                {!types.length ? <Typography.Text type="secondary">No activity this session</Typography.Text> : null}
            </Space>
            <div style={{ position: 'relative', overflowX: 'auto' }} onMouseLeave={() => setHover(null)}>
                <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label="Assignment activity">
                    {slots.map((slot, index) => {
                        const x = pad.left + slotWidth * index + (slotWidth - barWidth) / 2;
                        let y = pad.top + innerHeight;
                        const showLabel = index % labelEvery === 0 || index === slots.length - 1;
                        return (
                            <g key={slot.start} onMouseEnter={() => setHover(index)}>
                                <rect x={pad.left + slotWidth * index} y={pad.top} width={slotWidth} height={innerHeight} fill="transparent" />
                                {types.map(type => {
                                    const value = slot.counts[type] || 0;
                                    const barHeight = (value / max) * innerHeight;
                                    y -= barHeight;
                                    if (!barHeight) return null;
                                    return (
                                        <rect
                                            key={type}
                                            x={x}
                                            y={y}
                                            width={barWidth}
                                            height={barHeight}
                                            fill={ACTIVITY_COLORS[type] || '#98a2b3'}
                                        />
                                    );
                                })}
                                {showLabel ? (
                                    <text
                                        x={pad.left + slotWidth * index + slotWidth / 2}
                                        y={height - 6}
                                        textAnchor="middle"
                                        fontSize="10"
                                        fill="#667085"
                                    >
                                        {hourLabel(slot.start)}
                                    </text>
                                ) : null}
                            </g>
                        );
                    })}
                </svg>
                {hovered ? (
                    <div
                        style={{
                            position: 'absolute',
                            left: 8,
                            top: 8,
                            background: '#fff',
                            border: '1px solid #e4e7ec',
                            borderRadius: 4,
                            padding: '6px 8px',
                            fontSize: 12,
                        }}
                    >
                        <div>{hourLabel(hovered.start)} IST</div>
                        {types.filter(type => hovered.counts[type]).map(type => (
                            <div key={type}>
                                {activityLabel(type)}: {hovered.counts[type]}
                            </div>
                        ))}
                        {!stackTotal(hovered.counts) ? <div>No activity</div> : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

const AGE_COLUMNS = [
    { key: 'under1d', title: 'Under 1 day' },
    { key: 'd1to2', title: '1–2 days' },
    { key: 'd2to3', title: '2–3 days' },
    { key: 'd3to7', title: '3–7 days' },
    { key: 'd7to14', title: '1–2 weeks' },
    { key: 'over14d', title: 'Over 2 weeks' },
];

export default function AssignmentPerformance() {
    const [companyCodes, setCompanyCodes] = useState(loadCompanyCodes);
    const { data: companyCodeList } = useListingCompanies();
    const queryCodes = [...companyCodes].sort().join(',');
    const { data, isFetching } = useAssignmentPerformance(queryCodes ? { companyCodes: queryCodes } : {});

    const chooseCompanies = codes => {
        const next = codes || [];
        setCompanyCodes(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    };
    const payload = data?.data?.data || {};
    const summary = payload.summary || {};
    const updated = payload.generatedAt
        ? new Date(payload.generatedAt).toLocaleTimeString('en-GB', {
            hour: 'numeric',
            minute: 'numeric',
            hourCycle: 'h12',
        })
        : '';

    const clientColumns = [
        { title: 'Client', dataIndex: 'companyCode', fixed: 'left', width: 100 },
        ...AGE_COLUMNS.map(column => ({
            title: column.title,
            dataIndex: column.key,
            width: 120,
            sorter: (a, b) => (a[column.key] || 0) - (b[column.key] || 0),
        })),
        {
            title: 'Total',
            dataIndex: 'total',
            width: 90,
            defaultSortOrder: 'descend',
            sorter: (a, b) => (a.total || 0) - (b.total || 0),
        },
    ];

    return (
        <div style={{ padding: 16 }}>
            <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                    Assignment performance
                </Typography.Title>
                <Space>
                    {updated ? <Typography.Text type="secondary">Updated {updated}</Typography.Text> : null}
                    <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        placeholder="All clients"
                        style={{ minWidth: 280 }}
                        value={companyCodes}
                        onChange={chooseCompanies}
                        optionFilterProp="children"
                        maxTagCount={2}
                    >
                        {(companyCodeList || []).map(company => (
                            <Select.Option key={company._id} value={company.code}>
                                {company.code}
                            </Select.Option>
                        ))}
                    </Select>
                </Space>
            </Space>
            <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
                An assignment counts as done when the SKU is DD approved. Credit goes to the person who last assigned the model or angle preset.
                The session day runs from 6:00 am IST to 6:00 am IST the next morning. Waiting for DD is shootable SKUs from the last 12 months that are not support and not with the client for clarification. TSX is left out of every waiting count.
            </Typography.Paragraph>
            <Space size="large" wrap style={{ marginBottom: 16 }}>
                <Statistic title="Working now" value={summary.working || 0} />
                <Statistic title="Assigned today" value={summary.assignedToday || 0} />
                <Statistic title="Approved last hour" value={summary.approvedLastHour || 0} />
                <Statistic title="Approved today" value={summary.approvedToday || 0} />
                <Statistic title="Waiting for DD" value={summary.pending || 0} />
                <Statistic title="Waiting for client clarification" value={summary.clarification || 0} />
            </Space>
            {summary.unattributedToday ? (
                <Typography.Paragraph type="secondary">
                    {summary.unattributedToday} approved today {summary.unattributedToday === 1 ? 'is' : 'are'} not tied to a person, because no model or preset assignment was recorded.
                </Typography.Paragraph>
            ) : null}
            <Tabs
                items={[
                    {
                        key: 'people',
                        label: 'People',
                        children: (
                            <Table
                                loading={isFetching}
                                rowKey="userId"
                                dataSource={payload.operators || []}
                                pagination={false}
                                scroll={{ x: 980 }}
                                expandable={{
                                    expandedRowRender: row => <ActivityChart histogram={row.histogram} />,
                                    rowExpandable: row => (row.histogram || []).some(slot => stackTotal(slot.counts) > 0),
                                }}
                                columns={[
                                    {
                                        title: 'User',
                                        render: (_, row) => (
                                            <div>
                                                <div>{row.name || '—'}</div>
                                                <Typography.Text type="secondary">{row.email}</Typography.Text>
                                            </div>
                                        ),
                                    },
                                    {
                                        title: 'Status',
                                        width: 110,
                                        render: (_, row) => (
                                            <Tag color={row.status === 'Working' ? 'green' : 'default'}>{row.status}</Tag>
                                        ),
                                    },
                                    { title: 'Current task', render: (_, row) => currentTask(row) },
                                    { title: 'Assigned today', dataIndex: 'assignedToday', width: 130 },
                                    { title: 'Approved last hour', dataIndex: 'approvedHour', width: 150 },
                                    { title: 'Approved today', dataIndex: 'approvedToday', width: 140 },
                                    { title: 'Approved this week', dataIndex: 'approvedWeek', width: 160 },
                                ]}
                            />
                        ),
                    },
                    {
                        key: 'pending',
                        label: 'Waiting by time',
                        children: (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                <div>
                                    <Statistic title="Waiting for client clarification" value={summary.clarification || 0} />
                                    <Typography.Text type="secondary">
                                        DD is still pending, and the client has an open clarification. These SKUs are not included below.
                                    </Typography.Text>
                                </div>
                                <Last24Hours slots={payload.last24Hours} />
                                <Table
                                    loading={isFetching}
                                    rowKey="key"
                                    pagination={false}
                                    dataSource={payload.pendingByAge || []}
                                    columns={[
                                        { title: 'Waiting', dataIndex: 'label' },
                                        { title: 'SKUs', dataIndex: 'count', width: 120 },
                                    ]}
                                />
                                {(companyCodes.length > 1
                                    ? (payload.pendingByClient || []).length > 0
                                    : (payload.pendingByClient || []).length > 1) ? (
                                    <Table
                                        loading={isFetching}
                                        rowKey="companyCode"
                                        pagination={false}
                                        scroll={{ x: 980 }}
                                        dataSource={payload.pendingByClient}
                                        columns={clientColumns}
                                    />
                                ) : null}
                            </Space>
                        ),
                    },
                ]}
            />
        </div>
    );
}
